'use client';

import React from 'react';
import { cn } from '@/lib/utils';

interface MarkdownComponentsOptions {
  isUser?: boolean;
  onNavigate?: (href: string) => void;
}

export function getMarkdownComponents({ isUser = false, onNavigate }: MarkdownComponentsOptions = {}) {
  return {
    // ── Paragraph ─────────────────────────────────────────────────────────────
    p: ({ children }: any) => (
      <p className="mb-2 last:mb-0 text-[14px] leading-relaxed whitespace-pre-wrap break-words">
        {children}
      </p>
    ),

    // ── Code block container ───────────────────────────────────────────────────
    // react-markdown v9: pre wraps block code, code handles both inline and block.
    // We handle the outer container here and extract the language label from the
    // inner <code> child's className so we never create a nested <pre>.
    pre: ({ children }: any) => {
      const codeEl = React.isValidElement(children) ? children : null;
      const lang =
        /language-(\w+)/.exec((codeEl?.props as any)?.className ?? '')?.[1] ?? '';

      return (
        <div className="not-prose my-4 rounded-xl overflow-hidden bg-slate-900 border border-white/[0.07] shadow-md">
          {lang && (
            <div className="flex items-center px-4 py-1.5 bg-gray-900 border-b border-white/[0.07]">
              <span className="text-[11px] font-mono font-medium text-gray-400 select-none">
                {lang}
              </span>
            </div>
          )}
          <pre className="overflow-x-auto p-4 m-0 bg-transparent leading-relaxed">{children}</pre>
        </div>
      );
    },

    // ── Code (inline vs. block content) ───────────────────────────────────────
    // Block code (inside <pre>): has language-* className OR multi-line content.
    // Inline code: no className, single-line.
    code: ({ className, children, ...props }: any) => {
      const hasLanguage = /language-\w+/.test(className ?? '');
      const rawContent = Array.isArray(children) ? children.join('') : String(children ?? '');
      const isBlockCode = hasLanguage || rawContent.includes('\n');

      if (isBlockCode) {
        return (
          <code
            className={cn('font-mono text-[13px] leading-relaxed text-slate-100', className)}
            {...props}
          >
            {children}
          </code>
        );
      }

      return (
        <code
          className={
            isUser
              ? 'bg-white/20 text-white px-1.5 py-0.5 rounded text-[0.85em] font-mono'
              : 'bg-slate-100 text-slate-700 border border-slate-200/80 px-1.5 py-0.5 rounded text-[0.85em] font-mono'
          }
          {...props}
        >
          {children}
        </code>
      );
    },

    // ── Lists ──────────────────────────────────────────────────────────────────
    // Nested ul/ol inside li get reduced top margin via arbitrary child selectors.
    ul: ({ children }: any) => (
      <ul className="my-2 list-disc pl-5 space-y-0.5 text-[14px] text-slate-800">{children}</ul>
    ),

    ol: ({ children }: any) => (
      <ol className="my-2 list-decimal pl-5 space-y-0.5 text-[14px] text-slate-800">{children}</ol>
    ),

    li: ({ children }: any) => (
      // [&>ul]/[&>ol] only adjust margin — not padding-left, which is already set
      // by the ul/ol handlers themselves and provides correct nested indentation.
      <li className="leading-relaxed [&>ul]:mt-1.5 [&>ul]:mb-0 [&>ol]:mt-1.5 [&>ol]:mb-0">
        {children}
      </li>
    ),

    // ── Headings ──────────────────────────────────────────────────────────────
    h1: ({ children }: any) => (
      <h1 className="!text-xl font-bold text-slate-900 mt-5 mb-3 pb-1.5 border-b border-slate-200 first:mt-0">
        {children}
      </h1>
    ),
    h2: ({ children }: any) => (
      <h2 className="!text-lg font-semibold text-slate-900 mt-4 mb-2 first:mt-0">{children}</h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="!text-[15px] font-semibold text-slate-800 mt-3 mb-1.5 first:mt-0">{children}</h3>
    ),
    h4: ({ children }: any) => (
      <h4 className="!text-[14px] font-semibold text-slate-800 mt-2.5 mb-1 first:mt-0">{children}</h4>
    ),
    h5: ({ children }: any) => (
      <h5 className="!text-[14px] font-medium text-slate-700 mt-2 mb-1 first:mt-0">{children}</h5>
    ),
    h6: ({ children }: any) => (
      <h6 className="!text-xs font-semibold text-slate-500 uppercase tracking-wide mt-2 mb-1 first:mt-0">
        {children}
      </h6>
    ),

    // ── Blockquote ────────────────────────────────────────────────────────────
    blockquote: ({ children }: any) => (
      <blockquote
        className={
          isUser
            ? 'not-prose border-l-4 border-white/40 bg-white/10 pl-4 pr-3 py-2 my-3 rounded-r-lg text-white/90 text-[14px] italic'
            : 'not-prose border-l-4 border-blue-300 bg-blue-50/60 pl-4 pr-3 py-2.5 my-3 rounded-r-lg text-slate-600 text-[14px] italic'
        }
      >
        {children}
      </blockquote>
    ),

    // ── Inline formatting ─────────────────────────────────────────────────────
    strong: ({ children }: any) => (
      <strong className="font-semibold text-slate-900">{children}</strong>
    ),

    em: ({ children }: any) => <em className="italic">{children}</em>,

    hr: () => <hr className="my-4 border-0 border-t border-slate-200" />,

    // ── Links ─────────────────────────────────────────────────────────────────
    a: ({ href, children }: any) => {
      // In-app navigation — renders as an inline button
      // Matches /nav: protocol AND bare internal paths (AI doesn't always use /nav: prefix)
      if (href && onNavigate) {
        let path: string | null = null;
        if (href.startsWith('/nav:')) {
          path = href.slice(5); // strip "/nav:"
        } else if (href.startsWith('/') && !href.startsWith('//')) {
          path = href;
        }
        if (path) {
          // Strip leading/trailing arrows and "Go to" prefix that the AI may include
          const cleanLinkText = (node: React.ReactNode): React.ReactNode => {
            if (typeof node === 'string') return node.replace(/^[\s→\u2192]+|[\s→\u2192]+$/g, '').replace(/^Go\s+to\s+/i, '').trim();
            if (Array.isArray(node)) return node.map(cleanLinkText);
            return node;
          };
          return (
            <span
              role="button"
              tabIndex={0}
              onClick={() => window.open(path!, '_blank', 'noopener,noreferrer')}
              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') window.open(path!, '_blank', 'noopener,noreferrer'); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[14px] font-medium text-black bg-[#F1F5FF] border border-[#8EAEFF] rounded-lg hover:bg-blue-100 hover:border-blue-400 transition-colors cursor-pointer"
            >
              {cleanLinkText(children)}
              <svg aria-hidden="true" width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                <path d="M9 1L13 5M13 5L9 9M13 5H1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          );
        }
      }

      // Video / media links — render as styled button (same style as nav links)
      const childText = typeof children === 'string' ? children : Array.isArray(children) ? children.map((c: any) => typeof c === 'string' ? c : '').join('') : '';
      const isVideoLink = href && (/\.(mp4|webm|mov)(\?|$)/i.test(href) || /watch\s*video/i.test(childText) || /▶/.test(childText));

      if (isVideoLink) {
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[14px] font-medium text-black bg-[#F1F5FF] border border-[#8EAEFF] rounded-lg hover:bg-blue-100 hover:border-blue-400 transition-colors cursor-pointer no-underline"
          >
            {children}
            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        );
      }

      return (
        <a
          href={href}
          className={
            isUser
              ? 'text-white underline decoration-white/50 hover:decoration-white transition-colors'
              : 'text-blue-600 hover:text-blue-700 underline decoration-blue-300 hover:decoration-blue-500 transition-colors'
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      );
    },

    // ── Tables ────────────────────────────────────────────────────────────────
    table: ({ children }: any) => (
      <div className="not-prose overflow-x-auto my-4 rounded-lg border border-slate-200 shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">{children}</table>
      </div>
    ),

    thead: ({ children }: any) => <thead className="bg-slate-50">{children}</thead>,

    tbody: ({ children }: any) => (
      <tbody className="divide-y divide-slate-100 bg-white">{children}</tbody>
    ),

    tr: ({ children }: any) => (
      <tr className="hover:bg-slate-50/70 transition-colors">{children}</tr>
    ),

    th: ({ children }: any) => (
      <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
        {children}
      </th>
    ),

    td: ({ children }: any) => (
      <td className="px-3 py-2.5 text-[14px] text-slate-700">{children}</td>
    ),
  };
}
